import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormControlStatus } from '@angular/forms';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { I18nService } from '@core/i18n-legacy';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import { TellentDevFlag, TellentOrgAbilities } from '@core/tellent-types';
import {
  BoxComponent,
  CheckboxComponent,
  HeadingComponent,
  ProtectedControlDirective,
  RadioComponent,
} from '@core/user-interface-legacy';

import type { AbilityGroupViewModel, SimplifiedMember } from './types';
import { AbilityEditLevel } from './types';
import {
  buildGroups,
  canChangeMemberPermissions,
  describeGroup,
  hasTellentAbility,
  isSelf,
} from './view-model';

type Translations = {
  groups: Record<AbilityEditLevel, { heading: string; description: string }>;
  abilities: Record<
    TellentOrgAbilities,
    {
      name: string;
      description: string;
    }
  >;
};

@Component({
  selector: 'tla-edit-member-permissions',
  templateUrl: 'edit-member-permissions.component.html',
  styleUrl: './edit-member-permissions.component.less',
  imports: [
    CheckboxComponent,
    ReactiveFormsModule,
    HeadingComponent,
    BoxComponent,
    RadioComponent,
    ProtectedControlDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditMemberPermissionsComponent {
  private readonly groupsFlag = injectDevFlagSignal(TellentDevFlag.UnifiedGroups);
  private readonly departmentsFlag = injectDevFlagSignal(TellentDevFlag.UnifiedDepartments);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly form = this.createForm();
  public readonly groupDefinitions = computed(() => [
    describeGroup(AbilityEditLevel.Admin, {
      abilities: [
        {
          id: TellentOrgAbilities.ManageTellent,
          hidden: true,
          getControlState: data => ({
            value: true,
            disabled: !canChangeMemberPermissions(data),
          }),
        },
        {
          id: TellentOrgAbilities.ManageUsers,
          hidden: false,
          getControlState: () => ({ value: true, disabled: true }),
        },
        {
          id: TellentOrgAbilities.ManageBilling,
          hidden: false,
          getControlState: () => ({ value: true, disabled: true }),
        },
      ],
      canView: () => true,
      canEdit: data => canChangeMemberPermissions(data),
      match: ({ member }) => hasTellentAbility(member),
    }),
    describeGroup(AbilityEditLevel.User, {
      abilities: [
        {
          id: TellentOrgAbilities.ManageUsers,
          hidden: false,
          getControlState: () => ({
            value: this.memberHasAbility(TellentOrgAbilities.ManageUsers),
            disabled: false,
          }),
        },
        {
          id: TellentOrgAbilities.ManageBilling,
          hidden: false,
          getControlState: () => ({
            value: this.memberHasAbility(TellentOrgAbilities.ManageBilling),
            disabled: false,
          }),
        },
      ],
      canView: data => (data.admin.owner ? !isSelf(data) : true),
      canEdit: data => canChangeMemberPermissions(data),
      match: ({ member }) => !hasTellentAbility(member) && member.abilities.length > 0,
    }),
    describeGroup(AbilityEditLevel.None, {
      abilities: [],
      canView: data => (data.admin.owner ? !isSelf(data) : true),
      canEdit: data => canChangeMemberPermissions(data),
      match: () => true,
    }),
  ]);
  public readonly texts: Translations = {
    groups: {
      [AbilityEditLevel.Admin]: {
        heading: this.i18n.translate('tlc.members.edit_member.permission_tab.full_access.heading'),
        description: this.i18n.translate(
          'tlc.members.edit_member.permission_tab.full_access.description',
        ),
      },
      [AbilityEditLevel.User]: {
        heading: this.i18n.translate(
          'tlc.members.edit_member.permission_tab.administrative_access.heading',
        ),
        description: this.i18n.translate(this.administrativeAccessDescription),
      },
      [AbilityEditLevel.None]: {
        heading: this.i18n.translate(
          'tlc.members.edit_member.permission_tab.no_permissions.heading',
        ),
        description: this.i18n.translate(
          'tlc.members.edit_member.permission_tab.no_permissions.description',
        ),
      },
    },
    abilities: {
      [TellentOrgAbilities.ManageTellent]: {
        name: this.i18n.translate(
          'tlc.members.edit_member.permission_tab.permission_card.manage_tellent.subheading',
        ),
        description: this.i18n.translate(
          'tlc.members.edit_member.permission_tab.permission_card.manage_tellent.description',
        ),
      },
      [TellentOrgAbilities.ManageUsers]: {
        name: this.i18n.translate(
          'tlc.members.edit_member.permission_tab.permission_card.manage_users.subheading',
        ),
        description: this.i18n.translate(this.manageUsersDescription),
      },
      [TellentOrgAbilities.ManageBilling]: {
        name: this.i18n.translate(
          'tlc.members.edit_member.permission_tab.permission_card.manage_billing.subheading',
        ),
        description: this.i18n.translate(
          'tlc.members.edit_member.permission_tab.permission_card.manage_billing.description',
        ),
      },
    },
  };

  public admin = input.required<SimplifiedMember>();
  public member = input.required<SimplifiedMember>();

  public valueChanges = output<TellentOrgAbilities[]>();
  public statusChanges = output<{ status: FormControlStatus; pristine: boolean }>();
  public selectedGroupChanges = output<AbilityGroupViewModel | null | undefined>();

  public selectedGroup = signal<AbilityGroupViewModel | null>(null);
  public matchingGroup = computed(() => (this.groups() ? this.groups()!.find(g => g.match) : null));
  public activeGroup = computed(() => this.selectedGroup() || this.matchingGroup());

  public groups = computed(() =>
    buildGroups(this.groupDefinitions(), {
      admin: this.admin(),
      member: this.member(),
    }),
  );

  constructor() {
    let initial = true;
    effect(() => {
      this.runUpdateForm(initial);
      initial = false;
    });
  }

  public ngOnInit() {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.valueChanges.emit(this.getNormalizedFormValue()));

    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(status => {
      this.statusChanges.emit({ status, pristine: this.form.pristine });
    });

    this.selectedGroupChanges.emit(this.activeGroup());
  }

  public setGroupOnClicked(group: AbilityGroupViewModel) {
    if (group.canEdit) {
      this.selectedGroup.set(group);
      this.selectedGroupChanges.emit(group);
    }
  }

  private runUpdateForm(initial = true) {
    const admin = this.admin();
    const member = this.member();
    const group = this.activeGroup();

    if (member && group) {
      const updateOpts = { emitEvent: false };

      Object.entries(this.form.controls).forEach(
        ([name, control]: [TellentOrgAbilities, FormControl<boolean>]) => {
          const abilityConfig = group.abilities.find(({ id }) => name === id);

          if (abilityConfig) {
            control.reset(abilityConfig.getControlState({ member, admin }), updateOpts);
          } else {
            control.reset(
              {
                value: false,
                disabled:
                  group.abilities.length === 0
                    ? !canChangeMemberPermissions({ admin, member })
                    : true,
              },
              updateOpts,
            );
          }

          if (!initial) control.markAsDirty(updateOpts);
        },
      );

      this.form.updateValueAndValidity();
    }
  }

  private getNormalizedFormValue() {
    return <TellentOrgAbilities[]>Object.entries(this.form.value)
      .filter(([_, value]) => value)
      .map(([k]) => k);
  }

  private createForm() {
    return new FormGroup(
      <Record<TellentOrgAbilities, FormControl<boolean | null>>>(
        Object.fromEntries(
          Object.values(TellentOrgAbilities).map(ability => [
            ability,
            new FormControl({ value: null, disabled: true }),
          ]),
        )
      ),
    );
  }

  private memberHasAbility(ability: TellentOrgAbilities): boolean {
    return this.member().abilities.includes(ability);
  }

  private get administrativeAccessDescription() {
    if (this.departmentsFlag() && this.groupsFlag()) {
      return 'tlc.members.edit_member.permission_tab.administrative_access.description_groups_and_departments';
    }
    if (this.departmentsFlag()) {
      return 'tlc.members.edit_member.permission_tab.administrative_access.description_departments';
    }
    if (this.groupsFlag()) {
      return 'tlc.members.edit_member.permission_tab.administrative_access.description_groups';
    }
    return 'tlc.members.edit_member.permission_tab.administrative_access.description';
  }

  private get manageUsersDescription() {
    if (this.departmentsFlag() && this.groupsFlag()) {
      return 'tlc.members.edit_member.permission_tab.permission_card.manage_users.description_groups_and_departments';
    }
    if (this.departmentsFlag()) {
      return 'tlc.members.edit_member.permission_tab.permission_card.manage_users.description_departments';
    }
    if (this.groupsFlag()) {
      return 'tlc.members.edit_member.permission_tab.permission_card.manage_users.description_groups';
    }
    return 'tlc.members.edit_member.permission_tab.permission_card.manage_users.description';
  }
}
